/**
 * LLM Chat Streaming Service
 * Handles real-time chat streaming with SSE for chain-of-thought UI
 * Matches extension's streaming implementation
 */

import TokenManager from "./token-manager";
import {
  ChatStreamEvent,
  StreamingCallbacks,
  StreamMessageRequest,
} from "../../types/chat-stream";

// LLM API Base URL - required for chat streaming
const LLM_BASE_URL = import.meta.env.VITE_LLM_URL;

if (!LLM_BASE_URL) {
  console.error("VITE_LLM_URL is not configured in environment variables");
}

export class LLMChatStreamService {
  private abortController: AbortController | null = null;

  /**
   * Send message with streaming response via SSE
   * Returns a cleanup function to abort the stream
   */
  async sendMessageStream(
    request: StreamMessageRequest,
    callbacks: StreamingCallbacks,
  ): Promise<() => void> {
    // Create abort controller for this stream
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    const formData = new FormData();
    formData.append("chat_id", request.chat_id);

    // Send message as plain text (API expects string, not JSON array)
    formData.append("message", request.message);

    // Context handling
    formData.append("is_context", request.context ? "true" : "false");
    formData.append(
      "context",
      request.context ? JSON.stringify(request.context) : "{}",
    );

    // Add files if present
    if (request.files && request.files.length > 0) {
      request.files.forEach((file) => {
        formData.append("files", file);
      });
    }

    const token = TokenManager.getAccessToken();
    if (!token) {
      callbacks.onError("Authentication token not found. Please log in again.");
      return () => {};
    }

    console.log("Starting chat stream:", {
      chat_id: request.chat_id,
      message: request.message.substring(0, 100),
      files: request.files?.length || 0,
    });

    try {
      const response = await fetch(`${LLM_BASE_URL}/chat_stream`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-App-Source": "extension",
        },
        body: formData,
        signal,
      });

      if (!response.ok) {
        const errorText = await response
          .text()
          .catch(() => response.statusText);
        callbacks.onError(`Stream request failed: ${errorText}`);
        return () => {};
      }

      if (!response.body) {
        callbacks.onError("Response body is null");
        return () => {};
      }

      console.log("Streaming connection established");

      // Process SSE stream
      this.processStream(response.body, callbacks, signal);
    } catch (error: unknown) {
      if (error instanceof Error && error.name === "AbortError") {
        console.log("Stream aborted by user");
      } else {
        console.error("Stream error:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to start streaming";
        callbacks.onError(errorMessage);
      }
    }

    // Return cleanup function
    return () => this.abort();
  }

  /**
   * Process SSE stream and dispatch events to callbacks
   */
  private async processStream(
    body: ReadableStream<Uint8Array>,
    callbacks: StreamingCallbacks,
    signal: AbortSignal,
  ): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let currentEventType = "message";
    let fullResponseText = "";

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (signal.aborted) break;

        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // Keep the last partial line

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine) continue;

          // Capture the Event Type
          if (line.startsWith("event: ")) {
            currentEventType = line.slice(7).trim();
          }
          // Process the Data
          else if (line.startsWith("data: ")) {
            const data = line.slice(6).trim();
            if (data === "[DONE]") continue;

            try {
              const parsedData = JSON.parse(data);
              const event: ChatStreamEvent = {
                type: currentEventType as ChatStreamEvent["type"],
                ...parsedData,
              };

              this.handleStreamEvent(
                event,
                callbacks,
                fullResponseText,
                (newText) => {
                  fullResponseText = newText;
                },
              );

              // Check if stream is complete
              if (event.type === "complete" || event.type === "error") {
                return;
              }

              // Reset type for next message (standard SSE behavior)
              currentEventType = "message";
            } catch (parseError) {
              console.error("Failed to parse SSE event:", parseError);
            }
          }
        }
      }
    } catch (streamError: unknown) {
      if (signal.aborted) {
        console.log("Stream processing aborted");
      } else {
        console.error("Error processing stream:", streamError);
        const errorMessage =
          streamError instanceof Error
            ? streamError.message
            : "Stream processing failed";
        callbacks.onError(errorMessage);
      }
    }
  }

  /**
   * Handle individual stream events
   */
  private handleStreamEvent(
    event: ChatStreamEvent,
    callbacks: StreamingCallbacks,
    fullResponseText: string,
    setFullText: (text: string) => void,
  ): void {
    switch (event.type) {
      case "thought": {
        let statusMessage = "Thinking...";

        // API sends data as flat object, not nested in event.data
        const details = event.details || event.data?.details;
        const message = event.message || event.data?.message;

        if (details?.reason) {
          statusMessage = details.reason;
        } else if (details?.status) {
          statusMessage = details.status;
          if (details.tool) {
            statusMessage = `${details.tool}: ${statusMessage}`;
          }
        } else if (message) {
          statusMessage = message;
        }

        callbacks.onThought(statusMessage);
        break;
      }

      case "answer": {
        // API sends { content: "..." } in data field, which gets spread to event
        // So content is at event.content, not event.data.content
        const chunk = event.content || event.data?.content || "";
        const newFullText = fullResponseText + chunk;
        setFullText(newFullText);
        callbacks.onAnswer(
          chunk,
          newFullText,
          event.message_id || event.data?.message_id,
        );
        break;
      }

      case "complete": {
        const messageId = event.message_id || event.data?.message_id;
        callbacks.onComplete(messageId);
        break;
      }

      case "error": {
        callbacks.onError(
          event.error ||
            event.message ||
            event.data?.message ||
            "Stream error occurred",
          event.error_type,
        );
        break;
      }
    }
  }

  /**
   * Abort the current stream
   */
  abort(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }
}

// Export singleton instance
export const llmChatStreamService = new LLMChatStreamService();
